import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

// field: { name, label, type: text|number|textarea|select|list|json, options? }
export default function ResourceForm({ open, onOpenChange, fields, initial, onSave, title }) {
  const build = () => {
    const s = {};
    fields.forEach((f) => {
      let v = initial?.[f.name];
      if (f.type === "list") v = Array.isArray(v) ? v.join("\n") : "";
      else if (f.type === "json") v = v ? JSON.stringify(v, null, 2) : (f.default || "");
      else v = v ?? "";
      s[f.name] = v;
    });
    return s;
  };
  const [form, setForm] = useState(build);
  const [err, setErr] = useState("");

  // rebuild when initial changes
  const [lastId, setLastId] = useState(initial?.id);
  if (initial?.id !== lastId) { setLastId(initial?.id); setForm(build()); setErr(""); }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = () => {
    const out = {};
    try {
      fields.forEach((f) => {
        const raw = form[f.name];
        if (f.type === "list") out[f.name] = (raw || "").split("\n").map((x) => x.trim()).filter(Boolean);
        else if (f.type === "json") out[f.name] = raw ? JSON.parse(raw) : (f.default ? JSON.parse(f.default) : null);
        else if (f.type === "number") out[f.name] = raw === "" ? null : Number(raw);
        else out[f.name] = raw;
      });
    } catch (e) {
      setErr("Invalid JSON in one of the advanced fields: " + e.message);
      return;
    }
    onSave(out);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          {fields.map((f) => (
            <div key={f.name} className="space-y-1.5">
              <Label>{f.label}</Label>
              {f.type === "textarea" || f.type === "list" || f.type === "json" ? (
                <Textarea rows={f.type === "json" ? 8 : 4} value={form[f.name]} onChange={(e) => set(f.name, e.target.value)}
                  className={f.type === "json" ? "font-mono text-xs" : ""}
                  placeholder={f.type === "list" ? "One item per line" : f.type === "json" ? "JSON" : ""}
                  data-testid={`field-${f.name}`} />
              ) : f.type === "select" ? (
                <Select value={form[f.name]?.toString()} onValueChange={(v) => set(f.name, v)}>
                  <SelectTrigger data-testid={`field-${f.name}`}><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input type={f.type === "number" ? "number" : "text"} value={form[f.name]}
                  onChange={(e) => set(f.name, e.target.value)} data-testid={`field-${f.name}`} />
              )}
            </div>
          ))}
          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-mlily-blue hover:bg-mlily-bluehover" onClick={save} data-testid="resource-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
